import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Authentic, specific testimonials — no star ratings, no AI-sounding language
const testimonials = [
  {
    text: "Got the cotton coord set last month. Wore it to an office presentation — three colleagues asked where it was from. Wash after wash, the embroidery is still intact. Already ordered a second one.",
    name: "Divya S.",
    role: "Working professional",
    city: "Noida",
    source: "via WhatsApp DM",
    color: "bg-[#2D4A2D]",
  },
  {
    text: "Was nervous ordering without trying on. The fabric details they shared helped me choose. Kota Doria kurti arrived in 8 days, beautifully packed. Exactly as described — no nasty surprises.",
    name: "Riddhima K.",
    role: "Homemaker",
    city: "Bengaluru",
    source: "via Instagram",
    color: "bg-[#C9A84C]",
    textColor: "text-[#1A2A1A]",
  },
  {
    text: "I'm from Lucknow. I know what real Chikankari looks and feels like. The phanda and shadow work on their pieces is authentic — not the cheap synthetic imitation you find in malls.",
    name: "Ankita J.",
    role: "Teacher",
    city: "Lucknow",
    source: "via WhatsApp",
    color: "bg-[#1A2A1A]",
  },
  {
    text: "My mother wore Chikankari her whole life. I wanted something that felt like her collection — not mass market. This is exactly that. The murri work on my kurta is just right.",
    name: "Priya M.",
    role: "Doctor",
    city: "Mumbai",
    source: "via Google",
    color: "bg-[#2D4A2D]",
  },
  {
    text: "Sourcing for my boutique for 5 months. Consistent quality, good MOQ flexibility. Three of my regular customers now order directly from Weaves of Lucknow — which says everything.",
    name: "Namrata Shah",
    role: "Boutique owner",
    city: "Pune",
    source: "via WhatsApp DM",
    color: "bg-[#C9A84C]",
    textColor: "text-[#1A2A1A]",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } }
};

function Avatar({ name, color, textColor }) {
  const initials = name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div className={`w-9 h-9 rounded-full ${color} flex items-center justify-center flex-shrink-0`}>
      <span className={`font-sans font-semibold text-xs ${textColor || 'text-[#F5F0E8]'}`}>{initials}</span>
    </div>
  );
}

export default function TestimonialsSection() {
  const [active, setActive] = useState(0);
  const prev = () => setActive(a => (a - 1 + testimonials.length) % testimonials.length);
  const next = () => setActive(a => (a + 1) % testimonials.length);

  return (
    <section id="testimonials" data-testid="testimonials" className="py-24 md:py-32 bg-[#F5F0E8]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">

        {/* Header */}
        <motion.div
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.1 } } }}
          initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="mb-14"
        >
          <motion.span variants={fadeUp} className="inline-block text-xs font-sans uppercase tracking-[0.25em] text-[#C9A84C] font-medium mb-4">
            Real customers. Real words.
          </motion.span>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <motion.h2 variants={fadeUp} className="font-serif font-light text-[#2D4A2D] text-4xl md:text-5xl leading-tight tracking-tight">
              What they say
            </motion.h2>
            <motion.p variants={fadeUp} className="font-sans font-light text-[#5C6B5C] text-base max-w-sm leading-relaxed">
              Unedited. From WhatsApp DMs, Instagram, and Google.
            </motion.p>
          </div>
        </motion.div>

        {/* Desktop — 3 column grid */}
        <div className="hidden md:grid md:grid-cols-3 gap-5 mb-5">
          {testimonials.slice(0, 3).map((t, i) => (
            <TestimonialCard key={t.name} t={t} i={i} />
          ))}
        </div>
        <div className="hidden md:grid md:grid-cols-2 gap-5">
          {testimonials.slice(3).map((t, i) => (
            <TestimonialCard key={t.name} t={t} i={i + 3} />
          ))}
        </div>

        {/* Mobile — Carousel */}
        <div className="md:hidden">
          <motion.div
            key={active}
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.35 }}
          >
            <TestimonialCard t={testimonials[active]} i={active} />
          </motion.div>
          <div className="flex items-center justify-between mt-5">
            <span className="font-sans text-xs text-[#5C6B5C]">{active + 1} / {testimonials.length}</span>
            <div className="flex gap-3">
              <button onClick={prev} data-testid="testimonial-prev" className="border border-[#D5CCBF] p-2.5 text-[#2D4A2D] hover:bg-[#2D4A2D] hover:text-[#F5F0E8] transition-colors">
                <ChevronLeft size={16} strokeWidth={1.5} />
              </button>
              <button onClick={next} data-testid="testimonial-next" className="border border-[#D5CCBF] p-2.5 text-[#2D4A2D] hover:bg-[#2D4A2D] hover:text-[#F5F0E8] transition-colors">
                <ChevronRight size={16} strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </div>

        {/* Trust note */}
        <motion.p
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="text-center font-sans font-light text-[#2D4A2D] text-sm mt-10 border-t border-[#D5CCBF] pt-8 max-w-2xl mx-auto leading-relaxed"
        >
          All reviews are from real customers shared directly via WhatsApp, Instagram DMs, or Google. Nothing is fabricated.
        </motion.p>
      </div>
    </section>
  );
}

function TestimonialCard({ t, i }) {
  return (
    <motion.div
      variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay: (i % 3) * 0.1 } } }}
      initial="hidden" whileInView="visible" viewport={{ once: true }}
      className="bg-[#F5F0E8] border border-[#D5CCBF] p-6 flex flex-col gap-5 hover:border-[#C9A84C] transition-colors duration-300 border-l-2 border-l-[#C9A84C]"
    >
      {/* Quote */}
      <p className="font-sans font-light text-[#1A2A1A] text-base leading-relaxed flex-1">
        "{t.text}"
      </p>

      {/* Footer */}
      <div className="flex items-center justify-between pt-3 border-t border-[#E8DED1]">
        <div className="flex items-center gap-2.5">
          <Avatar name={t.name} color={t.color} textColor={t.textColor} />
          <div>
            <div className="font-sans font-semibold text-[#2D4A2D] text-[15px] leading-tight">{t.name}</div>
            <div className="text-xs font-sans text-[#5C6B5C] leading-tight mt-0.5">{t.role} · {t.city}</div>
          </div>
        </div>
        <span className="text-[11px] font-sans font-medium text-[#2D4A2D] bg-[#E8DED1] px-2.5 py-1 leading-none">
          {t.source}
        </span>
      </div>
    </motion.div>
  );
}
